import { forceLogout } from '@/contexts/auth/force-logout'
import {
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
} from '@/contexts/auth/token-storage'
import { refreshToken } from '@/services/fintalk/services/fintalk-backoffice/auth/refresh-token'

/** Shared so N concurrent failures trigger a single refresh. */
let inFlight: Promise<string | null> | null = null

const runRefresh = async (): Promise<string | null> => {
  const currentRefreshToken = getRefreshToken()

  if (!currentRefreshToken) {
    forceLogout()
    return null
  }

  try {
    const tokens = await refreshToken(currentRefreshToken)

    if (!tokens?.access_token || !tokens?.refresh_token) {
      forceLogout()
      return null
    }

    setAuthTokens(tokens)

    return tokens.access_token
  } catch {
    // Revoked refresh token, malformed access token, network failure — none of
    // it is recoverable here.
    forceLogout()
    return null
  }
}

/**
 * Returns a usable access token, refreshing only when needed. Expiry is never
 * inspected locally — the backend is the only authority on validity, and the
 * client clock cannot be trusted.
 *
 * @param tokenUsed the token the failed request actually sent, so a refresh
 * already done by a sibling request is reused instead of repeated.
 * @returns the token to replay with, or `null` to give up.
 */
export const ensureFreshToken = async (
  tokenUsed: string | null,
): Promise<string | null> => {
  const currentToken = getAccessToken()

  if (currentToken && currentToken !== tokenUsed) {
    return currentToken
  }

  if (inFlight) {
    return inFlight
  }

  inFlight = runRefresh().finally(() => {
    inFlight = null
  })

  return inFlight
}
